// Demographics tab — geographic + language reach.
//
// 2026-05-27 redesign: replaced the bare-bars + table layout with
// hero stat cards, a top-3 country podium, an SVG donut for languages,
// and a colour-graded heatmap for the platform × language matrix.
// Honest framing preserved — country is still inferred from ccTLD +
// language; methodology card at the bottom explains the caveat.
//
// No new dependencies; all visualisations are inline SVG / CSS.
import React, { useEffect, useMemo, useState } from 'react';
import { geoNaturalEarth1, geoPath, geoGraticule10 } from 'd3';
import { bp_demographics } from '../../../services';
import { formatNumber, unwrap, Skeleton, EmptyState } from './_shared';
import { Icon } from './_icons';
import './brand-mentions.scss';

// Native script labels — a reader recognising Korean as "한국어" is the
// difference between "exotic locale" and "tagged correctly".
const LANG_DISPLAY = {
    en: 'English',   ja: '日本語 Japanese', ko: '한국어 Korean',
    zh: '中文 Chinese', es: 'Español Spanish', pt: 'Português',
    fr: 'Français',  de: 'Deutsch',       it: 'Italiano',
    nl: 'Nederlands', sv: 'Svenska',      no: 'Norsk',
    da: 'Dansk',     fi: 'Suomi',        pl: 'Polski',
    cs: 'Čeština',   el: 'Ελληνικά Greek', tr: 'Türkçe',
    ru: 'Русский Russian', uk: 'Українська', ar: 'العربية Arabic',
    he: 'עברית Hebrew', hi: 'हिन्दी Hindi', bn: 'বাংলা Bengali',
    th: 'ไทย Thai',   vi: 'Tiếng Việt',   id: 'Bahasa Indonesia',
    ms: 'Bahasa Melayu', tl: 'Tagalog', ur: 'اردو Urdu',
    fa: 'فارسی Persian',
    unknown: 'Unknown',
};

const FLAGS = {
    US: '🇺🇸', GB: '🇬🇧', CA: '🇨🇦', AU: '🇦🇺', NZ: '🇳🇿', IE: '🇮🇪',
    DE: '🇩🇪', FR: '🇫🇷', IT: '🇮🇹', ES: '🇪🇸', PT: '🇵🇹', NL: '🇳🇱',
    BE: '🇧🇪', CH: '🇨🇭', AT: '🇦🇹', SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰',
    FI: '🇫🇮', PL: '🇵🇱', CZ: '🇨🇿', GR: '🇬🇷', RU: '🇷🇺', UA: '🇺🇦',
    TR: '🇹🇷', JP: '🇯🇵', KR: '🇰🇷', CN: '🇨🇳', TW: '🇹🇼', HK: '🇭🇰',
    SG: '🇸🇬', TH: '🇹🇭', VN: '🇻🇳', PH: '🇵🇭', ID: '🇮🇩', MY: '🇲🇾',
    IN: '🇮🇳', BD: '🇧🇩', PK: '🇵🇰', BR: '🇧🇷', MX: '🇲🇽', AR: '🇦🇷',
    CL: '🇨🇱', CO: '🇨🇴', PE: '🇵🇪', VE: '🇻🇪', ZA: '🇿🇦', NG: '🇳🇬',
    KE: '🇰🇪', EG: '🇪🇬', IL: '🇮🇱', AE: '🇦🇪', SA: '🇸🇦', XX: '🌐',
};

// Lightweight region grouping for the "reached X regions" stat. ISO-A2
// codes lifted to AMER / EMEA / APAC. Anything not mapped lands in
// OTHER, which still counts as a region for diversity purposes.
const REGION_OF = {
    US: 'AMER', CA: 'AMER', MX: 'AMER', BR: 'AMER', AR: 'AMER',
    CL: 'AMER', CO: 'AMER', PE: 'AMER', VE: 'AMER',
    GB: 'EMEA', IE: 'EMEA', DE: 'EMEA', FR: 'EMEA', IT: 'EMEA',
    ES: 'EMEA', PT: 'EMEA', NL: 'EMEA', BE: 'EMEA', CH: 'EMEA',
    AT: 'EMEA', SE: 'EMEA', NO: 'EMEA', DK: 'EMEA', FI: 'EMEA',
    PL: 'EMEA', CZ: 'EMEA', GR: 'EMEA', RU: 'EMEA', UA: 'EMEA',
    TR: 'EMEA', IL: 'EMEA', AE: 'EMEA', SA: 'EMEA', EG: 'EMEA',
    ZA: 'EMEA', NG: 'EMEA', KE: 'EMEA',
    JP: 'APAC', KR: 'APAC', CN: 'APAC', TW: 'APAC', HK: 'APAC',
    SG: 'APAC', TH: 'APAC', VN: 'APAC', PH: 'APAC', ID: 'APAC',
    MY: 'APAC', IN: 'APAC', BD: 'APAC', PK: 'APAC', AU: 'APAC',
    NZ: 'APAC',
};

// Cool deterministic palette for the language donut. Cycles for >8
// languages; we only show 6 in the donut + "Other" bucket anyway.
const DONUT_PALETTE = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16',
];

// ---------------------------------------------------------------------
// Reusable visual atoms
// ---------------------------------------------------------------------

function StatCard({ label, value, sub, accent = '#3b82f6', icon }) {
    return (
        <div style={{
            flex: 1, minWidth: 180,
            padding: '14px 16px',
            background: '#ffffff',
            border: '1px solid rgba(148,163,184,0.30)',
            borderRadius: 10,
            position: 'relative', overflow: 'hidden',
        }}>
            {/* Subtle accent bar at top — wakes the card up without */}
            {/* shouting. */}
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
            }} />
            <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                textTransform: 'uppercase', color: '#64748b',
                marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
            }}>
                {icon && <Icon name={icon} size={14} color={accent} />}
                {label}
            </div>
            <div style={{
                fontSize: 26, fontWeight: 700, color: '#0f172a',
                lineHeight: 1.1, fontFamily: 'ui-sans-serif, system-ui',
            }}>
                {value}
            </div>
            {sub && (
                <div style={{
                    marginTop: 4, fontSize: 11, color: '#64748b',
                    lineHeight: 1.4,
                }}>
                    {sub}
                </div>
            )}
        </div>
    );
}

function CountryPodium({ countries, selectedIso, onSelectCountry }) {
    // Top 3 — gold / silver / bronze tint, larger flag, bigger %.
    if (!countries || countries.length === 0) return null;
    const top3 = countries.slice(0, 3);
    const TINTS = [
        { bg: 'linear-gradient(135deg, #fde68a, #fef3c7)', border: '#f59e0b', label: '#92400e' },
        { bg: 'linear-gradient(135deg, #e2e8f0, #f1f5f9)', border: '#94a3b8', label: '#475569' },
        { bg: 'linear-gradient(135deg, #fed7aa, #ffedd5)', border: '#ea580c', label: '#9a3412' },
    ];
    return (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            {top3.map((c, i) => {
                const isSelected = selectedIso === c.country_iso;
                return (
                <div key={c.country_iso || i}
                     onClick={() => onSelectCountry && onSelectCountry(c.country_iso)}
                     style={{
                        flex: 1, padding: '14px 14px 12px', borderRadius: 10,
                        background: TINTS[i].bg,
                        border: `1px solid ${isSelected ? '#1d4ed8' : TINTS[i].border + '55'}`,
                        boxShadow: isSelected ? '0 0 0 2px rgba(29,78,216,0.35)' : 'none',
                        cursor: 'pointer',
                        position: 'relative', overflow: 'hidden',
                     }}>
                    <div style={{
                        position: 'absolute', top: 8, right: 10,
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                        color: TINTS[i].label,
                        background: 'rgba(255,255,255,0.55)',
                        padding: '2px 6px', borderRadius: 999,
                    }}>
                        #{i + 1}
                    </div>
                    <div style={{ fontSize: 30, lineHeight: 1 }}>
                        {FLAGS[c.country_iso] || '🏳'}
                    </div>
                    <div style={{
                        marginTop: 6, fontSize: 13, fontWeight: 600,
                        color: '#0f172a',
                    }}>
                        {c.country_name}
                    </div>
                    <div style={{
                        marginTop: 2, display: 'flex', alignItems: 'baseline', gap: 8,
                    }}>
                        <span style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                            {c.share_pct}%
                        </span>
                        <span style={{ fontSize: 11, color: '#475569' }}>
                            {formatNumber(c.count)} mentions
                        </span>
                    </div>
                </div>
                );
            })}
            {/* Slot-fill placeholders if we have <3 countries — keeps the */}
            {/* row from looking lopsided. */}
            {[...Array(Math.max(0, 3 - top3.length))].map((_, i) => (
                <div key={`empty-${i}`}
                     style={{
                        flex: 1, padding: '14px 14px 12px', borderRadius: 10,
                        background: 'rgba(248,250,252,0.7)',
                        border: '1px dashed rgba(148,163,184,0.4)',
                        opacity: 0.6,
                     }}>
                    <div style={{ fontSize: 30, lineHeight: 1, color: '#cbd5e1' }}>🏳</div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                        Awaiting more sources
                    </div>
                </div>
            ))}
        </div>
    );
}

function CountryRow({ c, max, rank, isSelected, onSelect }) {
    const pct = max ? (c.count / max) * 100 : 0;
    const baseBg = isSelected ? 'rgba(219,234,254,0.9)' : 'transparent';
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px', borderRadius: 6,
            transition: 'background 100ms', cursor: 'pointer',
            background: baseBg,
            boxShadow: isSelected ? 'inset 3px 0 0 #1d4ed8' : 'none',
        }}
             onClick={() => onSelect && onSelect(c.country_iso)}
             onMouseEnter={e => e.currentTarget.style.background = 'rgba(241,245,249,0.7)'}
             onMouseLeave={e => e.currentTarget.style.background = baseBg}>
            <span style={{
                width: 22, fontSize: 11, fontWeight: 600,
                color: '#94a3b8', textAlign: 'right',
            }}>
                {rank}
            </span>
            <span style={{ width: 22, fontSize: 16, textAlign: 'center' }}>
                {FLAGS[c.country_iso] || '🏳'}
            </span>
            <span style={{ flex: 1, fontSize: 12, color: '#0f172a', fontWeight: 500 }}>
                {c.country_name}
            </span>
            <span style={{ width: 110, position: 'relative', height: 6 }}>
                <span style={{
                    display: 'block', position: 'absolute', inset: 0,
                    background: '#e2e8f0', borderRadius: 999,
                }} />
                <span style={{
                    display: 'block', position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: pct + '%',
                    background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                    borderRadius: 999,
                }} />
            </span>
            <span style={{
                width: 64, textAlign: 'right',
                fontSize: 12, color: '#334155', fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
            }}>
                {formatNumber(c.count)}
            </span>
            <span style={{
                width: 48, textAlign: 'right',
                fontSize: 11, color: '#64748b',
                fontVariantNumeric: 'tabular-nums',
            }}>
                {c.share_pct}%
            </span>
        </div>
    );
}

function LanguageDonut({ languages, total }) {
    // Cap at 6 slices + "Other" so the donut stays readable.
    const TOP_N = 6;
    const top = languages.slice(0, TOP_N);
    const restCount = languages.slice(TOP_N).reduce((s, l) => s + l.count, 0);
    const slices = restCount > 0
        ? [...top, { lang: '_other_', label: 'Other', count: restCount }]
        : top;
    const sum = slices.reduce((s, l) => s + l.count, 0) || 1;

    // SVG geometry — circle outlined with stroke-dasharray; each slice
    // is a partial-circle stroke with calculated offset. Donut is sized
    // to leave room for the legend inside the fixed 320px rail.
    const size = 168, stroke = 24, r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    let acc = 0;
    const arcs = slices.map((l, i) => {
        const frac = l.count / sum;
        const dash = `${frac * circ} ${circ}`;
        const offset = -acc * circ;
        acc += frac;
        return {
            ...l,
            color: DONUT_PALETTE[i % DONUT_PALETTE.length],
            dash, offset, frac,
        };
    });

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center',
                      justifyContent: 'center', gap: 16 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
                 style={{ flexShrink: 0 }}>
                <circle cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
                {arcs.map((a, i) => (
                    <circle key={i}
                            cx={size / 2} cy={size / 2} r={r}
                            fill="none" stroke={a.color} strokeWidth={stroke}
                            strokeDasharray={a.dash}
                            strokeDashoffset={a.offset}
                            transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                        <title>{(a.label || LANG_DISPLAY[a.lang] || a.lang)} — {a.count} ({(a.frac * 100).toFixed(1)}%)</title>
                    </circle>
                ))}
                {/* Centre label — total + "Languages" */}
                <text x={size / 2} y={size / 2 - 6}
                      textAnchor="middle" fontSize={26} fontWeight={700}
                      fill="#0f172a" fontFamily="ui-sans-serif, system-ui">
                    {languages.length}
                </text>
                <text x={size / 2} y={size / 2 + 16}
                      textAnchor="middle" fontSize={10} fontWeight={600}
                      letterSpacing={0.8} fill="#64748b">
                    LANGUAGES
                </text>
            </svg>
            <div style={{ flex: '1 1 200px', minWidth: 0, display: 'flex',
                          flexDirection: 'column', gap: 5 }}>
                {arcs.map((a, i) => (
                    <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        fontSize: 12, minWidth: 0,
                    }}>
                        <span style={{
                            width: 10, height: 10, borderRadius: 2,
                            background: a.color, flexShrink: 0,
                        }} />
                        <span style={{ flex: 1, minWidth: 0, color: '#0f172a', fontWeight: 500,
                                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={a.label || LANG_DISPLAY[a.lang] || a.lang}>
                            {a.label || LANG_DISPLAY[a.lang] || a.lang}
                        </span>
                        <span style={{
                            flexShrink: 0,
                            color: '#334155', fontVariantNumeric: 'tabular-nums',
                        }}>
                            {(a.frac * 100).toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PlatformLangHeatmap({ rows, total }) {
    // Pivot the (platform, lang, count) rows into a platform × lang
    // grid. Cell shade ∝ relative count within the platform's row,
    // so a quiet platform's hottest lang still pops.
    if (!rows || rows.length === 0) {
        return (
            <div style={{ color: '#94a3b8', fontSize: 12, padding: 30, textAlign: 'center' }}>
                No data
            </div>
        );
    }
    const platforms = Array.from(new Set(rows.map(r => r.platform)));
    // Top languages by global count, capped at 8 for visual budget.
    const langTotals = {};
    rows.forEach(r => { langTotals[r.lang] = (langTotals[r.lang] || 0) + r.count; });
    const langs = Object.entries(langTotals)
        .sort((a, b) => b[1] - a[1]).slice(0, 8).map(([l]) => l);
    const cellOf = {};
    rows.forEach(r => {
        cellOf[`${r.platform}::${r.lang}`] = r.count;
    });
    // Per-row max for shading
    const rowMax = {};
    platforms.forEach(p => {
        rowMax[p] = Math.max(1, ...langs.map(l => cellOf[`${p}::${l}`] || 0));
    });

    const shadeBg = (count, max) => {
        if (!count) return '#f8fafc';
        // Lerp from very-light blue to saturated blue based on count/max.
        const t = Math.max(0.08, count / max);
        // 226,232,240 → 29,78,216 in RGB
        const r = Math.round(226 + (29 - 226) * t);
        const g = Math.round(232 + (78 - 232) * t);
        const b = Math.round(240 + (216 - 240) * t);
        return `rgb(${r},${g},${b})`;
    };
    const fg = (count, max) => {
        if (!count) return '#cbd5e1';
        const t = count / max;
        return t > 0.55 ? '#ffffff' : '#0f172a';
    };

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{
                borderCollapse: 'separate', borderSpacing: 4,
                fontSize: 11, minWidth: '100%',
            }}>
                <thead>
                    <tr>
                        <th style={{
                            textAlign: 'left', padding: '4px 8px',
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                            color: '#64748b', textTransform: 'uppercase',
                        }}>
                            Platform
                        </th>
                        {langs.map(l => (
                            <th key={l} style={{
                                padding: '4px 8px', minWidth: 56,
                                textAlign: 'center',
                                fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                color: '#64748b', textTransform: 'uppercase',
                            }}>
                                {l}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {platforms.map(p => (
                        <tr key={p}>
                            <td style={{
                                padding: '6px 10px', fontWeight: 600, color: '#0f172a',
                                fontSize: 12, textTransform: 'capitalize',
                            }}>
                                {p}
                            </td>
                            {langs.map(l => {
                                const count = cellOf[`${p}::${l}`] || 0;
                                const max = rowMax[p];
                                return (
                                    <td key={l}
                                        title={count
                                            ? `${p} × ${LANG_DISPLAY[l] || l}: ${count} mentions (${total ? ((count / total) * 100).toFixed(1) : 0}% of all)`
                                            : `${p} × ${LANG_DISPLAY[l] || l}: 0 mentions`}
                                        style={{
                                            background: shadeBg(count, max),
                                            color: fg(count, max),
                                            padding: '8px 10px',
                                            borderRadius: 4,
                                            textAlign: 'center',
                                            fontVariantNumeric: 'tabular-nums',
                                            fontWeight: count ? 600 : 400,
                                            minWidth: 48,
                                        }}>
                                        {count || '·'}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* Legend strip */}
            <div style={{
                marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 10, color: '#64748b',
            }}>
                <span>fewer</span>
                <span style={{ display: 'inline-flex', height: 8, width: 160, borderRadius: 4,
                               background: 'linear-gradient(90deg, rgb(226,232,240), rgb(29,78,216))' }} />
                <span>more</span>
                <span style={{ marginLeft: 'auto' }}>
                    Cell shade scaled per-platform (row-relative).
                </span>
            </div>
        </div>
    );
}

// Approximate country centroids [lng, lat] for the bubble map. Covers the
// same set as FLAGS; countries without an entry simply don't get a bubble
// (they still appear in the ranked list below).
const CENTROIDS = {
    US: [-98, 39], GB: [-2, 54], CA: [-106, 56], AU: [134, -25], NZ: [174, -41],
    IE: [-8, 53], DE: [10, 51], FR: [2, 46], IT: [12, 42], ES: [-4, 40],
    PT: [-8, 39], NL: [5, 52], BE: [4, 50], CH: [8, 47], AT: [14, 47],
    SE: [15, 62], NO: [8, 61], DK: [10, 56], FI: [26, 64], PL: [19, 52],
    CZ: [15, 50], GR: [22, 39], RU: [100, 60], UA: [32, 49], TR: [35, 39],
    JP: [138, 36], KR: [128, 36], CN: [105, 35], TW: [121, 24], HK: [114, 22],
    SG: [104, 1], TH: [101, 15], VN: [106, 16], PH: [122, 13], ID: [120, -5],
    MY: [102, 4], IN: [79, 22], BD: [90, 24], PK: [70, 30], BR: [-51, -10],
    MX: [-102, 23], AR: [-64, -38], CL: [-71, -30], CO: [-74, 4], PE: [-76, -10],
    VE: [-66, 7], ZA: [24, -29], NG: [8, 10], KE: [38, 0], EG: [30, 27],
    IL: [35, 31], AE: [54, 24], SA: [45, 24],
};

function WorldBubbleMap({ countries, selectedIso, onSelectCountry }) {
    // Proportional-symbol world map: graticule + sphere frame (d3-geo
    // NaturalEarth projection) with a bubble per country, area ∝ mentions.
    // Hover highlights the bubble + shows a styled tooltip; click selects
    // the country and cross-highlights its row in the ranked list below.
    const W = 600, H = 300;
    const [hoverIso, setHoverIso] = useState(null);
    const projection = useMemo(
        () => geoNaturalEarth1().fitExtent([[6, 6], [W - 6, H - 6]], { type: 'Sphere' }),
        [],
    );
    const path = useMemo(() => geoPath(projection), [projection]);
    const max = Math.max(1, ...countries.map(c => c.count));
    const rMin = 4, rMax = 26;
    const bubbles = countries
        .map(c => {
            const ll = CENTROIDS[c.country_iso];
            if (!ll) return null;
            const xy = projection(ll);
            if (!xy) return null;
            return { ...c, x: xy[0], y: xy[1], r: rMin + Math.sqrt(c.count / max) * (rMax - rMin) };
        })
        .filter(Boolean)
        .sort((a, b) => b.r - a.r);  // larger first so small ones stay clickable on top
    const plotted = bubbles.length;
    const active = hoverIso || selectedIso;
    const hovered = bubbles.find(b => b.country_iso === hoverIso) || null;

    return (
        <div style={{ position: 'relative' }}>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}
                 role="img" aria-label="World map of mention origins">
                <path d={path({ type: 'Sphere' })} fill="#f8fafc" stroke="#e2e8f0" strokeWidth={1} />
                <path d={path(geoGraticule10())} fill="none" stroke="#e2e8f0" strokeWidth={0.5} />
                {bubbles.map((c, i) => {
                    const isActive = active === c.country_iso;
                    const isSelected = selectedIso === c.country_iso;
                    const dimmed = active && !isActive;
                    return (
                        <circle key={c.country_iso || i} cx={c.x} cy={c.y}
                                r={isActive ? c.r + 3 : c.r}
                                fill={isSelected ? '#1d4ed8' : '#3b82f6'}
                                fillOpacity={dimmed ? 0.18 : (isActive ? 0.82 : 0.5)}
                                stroke={isSelected ? '#1e3a8a' : '#1d4ed8'}
                                strokeWidth={isActive ? 2 : 1}
                                style={{ cursor: 'pointer', transition: 'r 120ms ease, fill-opacity 120ms ease' }}
                                onMouseEnter={() => setHoverIso(c.country_iso)}
                                onMouseLeave={() => setHoverIso(h => (h === c.country_iso ? null : h))}
                                onClick={() => onSelectCountry && onSelectCountry(c.country_iso)}>
                            <title>{c.country_name}: {formatNumber(c.count)} mentions ({c.share_pct}%)</title>
                        </circle>
                    );
                })}
                {/* Label the largest few bubbles with their ISO code. */}
                {bubbles.filter(c => c.r >= 11).slice(0, 6).map((c, i) => (
                    <text key={'l' + i} x={c.x} y={c.y + 3} textAnchor="middle"
                          fontSize={9} fontWeight={700} fill="#0f172a" pointerEvents="none">
                        {c.country_iso}
                    </text>
                ))}
            </svg>

            {hovered && (
                <div style={{
                    position: 'absolute',
                    left: `${(hovered.x / W) * 100}%`,
                    top: `${(hovered.y / H) * 100}%`,
                    transform: 'translate(-50%, calc(-100% - 10px))',
                    pointerEvents: 'none',
                    background: '#0f172a', color: '#ffffff',
                    borderRadius: 6, padding: '6px 9px',
                    fontSize: 11, lineHeight: 1.35, whiteSpace: 'nowrap',
                    boxShadow: '0 6px 18px rgba(15,23,42,0.28)', zIndex: 5,
                }}>
                    <div style={{ fontWeight: 700, marginBottom: 1 }}>
                        <span style={{ marginRight: 5 }}>{FLAGS[hovered.country_iso] || '🏳'}</span>
                        {hovered.country_name}
                    </div>
                    <div style={{ color: '#cbd5e1' }}>
                        {formatNumber(hovered.count)} mentions · {hovered.share_pct}%
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6,
                          fontSize: 10, color: '#64748b' }}>
                <span style={{ display: 'inline-flex', width: 10, height: 10, borderRadius: '50%',
                               background: '#3b82f6', opacity: 0.5, border: '1px solid #1d4ed8' }} />
                bubble size ∝ mentions · {plotted} of {countries.length} countries mapped
                {selectedIso && (
                    <button type="button"
                            onClick={() => onSelectCountry && onSelectCountry(selectedIso)}
                            style={{ marginLeft: 'auto', background: 'none', border: 'none',
                                     color: '#1d4ed8', fontSize: 10, fontWeight: 600,
                                     cursor: 'pointer', padding: 0 }}>
                        Clear {selectedIso} selection
                    </button>
                )}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------

export default function DemographicsTab() {
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [showAllCountries, setShowAllCountries] = useState(false);
    // Cross-component selection: clicking a bubble / podium card / list row
    // highlights that country everywhere. Click again to clear.
    const [selectedIso, setSelectedIso] = useState(null);
    const toggleCountry = (iso) => setSelectedIso(prev => (prev === iso ? null : iso));
    const [data, setData] = useState({
        total: 0, languages: [], countries: [], platform_x_lang: [],
    });

    useEffect(() => {
        setLoading(true);
        bp_demographics({ days }).then(r => {
            setData(unwrap(r) || { total: 0, languages: [], countries: [], platform_x_lang: [] });
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [days]);

    const maxCountryCount = Math.max(1, ...data.countries.map(c => c.count));

    // Derived stats for the hero row.
    const regionsTouched = useMemo(() => {
        const regions = new Set();
        data.countries.forEach(c => regions.add(REGION_OF[c.country_iso] || 'OTHER'));
        return regions.size;
    }, [data.countries]);

    const topCountry = data.countries[0];
    const topLanguage = data.languages[0];
    const nonEnglishShare = useMemo(() => {
        if (!data.languages.length) return 0;
        const nonEn = data.languages
            .filter(l => l.lang !== 'en' && l.lang !== 'unknown')
            .reduce((s, l) => s + l.count, 0);
        return data.total ? Math.round((nonEn / data.total) * 100) : 0;
    }, [data.languages, data.total]);

    return (
        <>
            <div className="bm_header">
                <div>
                    <div className="bm_subtitle" style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>
                        Demographics
                    </div>
                    <div className="bm_subtitle" style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                        Where your mentions come from — by country, language, and platform.
                        Inferred from post text + URL ccTLD; native geo metadata will replace this when available.
                    </div>
                </div>
            </div>

            <div className="bm_filters" style={{ marginBottom: 14 }}>
                <select value={days} onChange={e => setDays(Number(e.target.value))}>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last 12 months</option>
                </select>
            </div>

            {loading && (
                <div style={{ display: 'flex', gap: 12, padding: '4px 0 16px' }}>
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={{
                            flex: 1, padding: 14, background: '#ffffff',
                            border: '1px solid rgba(148,163,184,0.30)',
                            borderRadius: 10,
                        }}>
                            <Skeleton width="50%" height={10} />
                            <div style={{ marginTop: 8 }}>
                                <Skeleton width="70%" height={22} />
                            </div>
                            <div style={{ marginTop: 6 }}>
                                <Skeleton width="40%" height={9} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && data.total === 0 && (
                <EmptyState
                    mark="dots"
                    title="No demographic data yet"
                    body="Demographics are inferred from post language + URL ccTLD on captured mentions. Once the scanner has mentions for this org, languages and countries populate here automatically. Try adding more anchors in Settings or clicking Scan now on the Mentions tab."
                />
            )}

            {!loading && data.total > 0 && (
                <>
                    {/* ---------- Hero stat row ---------- */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                        <StatCard
                            label="Mentions analysed"
                            value={formatNumber(data.total)}
                            sub={`over the last ${days} day${days === 1 ? '' : 's'}`}
                            accent="#3b82f6" icon="bar-chart" />
                        <StatCard
                            label="Countries reached"
                            value={data.countries.length}
                            sub={`across ${regionsTouched} region${regionsTouched === 1 ? '' : 's'} (AMER / EMEA / APAC)`}
                            accent="#10b981" icon="globe" />
                        <StatCard
                            label="Languages detected"
                            value={data.languages.length}
                            sub={`${nonEnglishShare}% of mentions are non-English`}
                            accent="#f59e0b" icon="message" />
                        <StatCard
                            label="Top market"
                            value={topCountry
                                ? <span><span style={{ marginRight: 6 }}>{FLAGS[topCountry.country_iso] || '🏳'}</span>{topCountry.country_iso}</span>
                                : '—'}
                            sub={topCountry
                                ? `${topCountry.country_name} · ${topCountry.share_pct}% share`
                                : 'no data'}
                            accent="#8b5cf6" icon="award" />
                    </div>

                    {/* ---------- Geo + Languages ---------- */}
                    <div className="bm_grid" style={{ marginBottom: 16 }}>
                        <div className="bm_main">
                            <div className="bm_chart_card" style={{ padding: '14px 16px' }}>
                                <div style={{ display: 'flex', alignItems: 'baseline',
                                              justifyContent: 'space-between',
                                              marginBottom: 12 }}>
                                    <h4 style={{
                                        margin: 0, color: '#0f172a', fontSize: 13,
                                        textTransform: 'uppercase', letterSpacing: 0.6,
                                    }}>
                                        Geographic reach
                                    </h4>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                        ranked by mention share
                                    </span>
                                </div>
                                {data.countries.length > 0 && (
                                    <div style={{ marginBottom: 14 }}>
                                        <WorldBubbleMap countries={data.countries}
                                                        selectedIso={selectedIso}
                                                        onSelectCountry={toggleCountry} />
                                    </div>
                                )}
                                <CountryPodium countries={data.countries}
                                               selectedIso={selectedIso}
                                               onSelectCountry={toggleCountry} />
                                {data.countries.length > 3 && (
                                    <div style={{
                                        marginTop: 6, paddingTop: 10,
                                        borderTop: '1px solid rgba(148,163,184,0.25)',
                                    }}>
                                        {data.countries.slice(3, showAllCountries ? 30 : 9).map((c, i) => (
                                            <CountryRow key={c.country_iso} c={c}
                                                        max={maxCountryCount}
                                                        rank={i + 4}
                                                        isSelected={selectedIso === c.country_iso}
                                                        onSelect={toggleCountry} />
                                        ))}
                                        {data.countries.length > 9 && (
                                            <button type="button"
                                                    onClick={() => setShowAllCountries(s => !s)}
                                                    style={{
                                                        marginTop: 8, marginLeft: 54,
                                                        background: 'none', border: 'none',
                                                        color: '#1d4ed8', fontSize: 12,
                                                        fontWeight: 600, cursor: 'pointer', padding: 0,
                                                    }}>
                                                {showAllCountries
                                                    ? 'Show less'
                                                    : `Show ${data.countries.length - 9} more ${data.countries.length - 9 === 1 ? 'country' : 'countries'}`}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {data.countries.length === 0 && (
                                    <div className="bm_chart_empty">No data in this window.</div>
                                )}
                            </div>
                        </div>

                        <div className="bm_rail" style={{ gap: 12 }}>
                            <div className="bm_rail_card" style={{ padding: '14px 16px' }}>
                                <h4 style={{
                                    margin: 0, marginBottom: 12,
                                    color: '#0f172a', fontSize: 13,
                                    textTransform: 'uppercase', letterSpacing: 0.6,
                                }}>
                                    Language mix
                                </h4>
                                {data.languages.length > 0 ? (
                                    <LanguageDonut languages={data.languages} total={data.total} />
                                ) : (
                                    <div className="bm_rail_empty">No data</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ---------- Heatmap ---------- */}
                    <div className="bm_chart_card" style={{ padding: '14px 16px', marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline',
                                      justifyContent: 'space-between',
                                      marginBottom: 12 }}>
                            <h4 style={{
                                margin: 0, color: '#0f172a', fontSize: 13,
                                textTransform: 'uppercase', letterSpacing: 0.6,
                            }}>
                                Platform × language heatmap
                            </h4>
                            <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                where each conversation is happening
                            </span>
                        </div>
                        <PlatformLangHeatmap rows={data.platform_x_lang} total={data.total} />
                    </div>

                    {/* ---------- Methodology / honest caveat ---------- */}
                    <div style={{
                        padding: '12px 16px',
                        background: 'rgba(248,250,252,0.7)',
                        border: '1px solid rgba(148,163,184,0.25)',
                        borderLeft: '3px solid #3b82f6',
                        borderRadius: 6,
                        fontSize: 11.5, color: '#475569', lineHeight: 1.6,
                    }}>
                        <strong style={{ color: '#0f172a' }}>How we infer this.</strong>{' '}
                        Country detection prefers ccTLD match (high confidence) — e.g.
                        a <code>reddit.com/r/de/...</code> URL maps to Germany. When
                        ccTLD is absent, country is inferred from detected language
                        (accurate for non-English text, defaults to US for English).
                        Mentions with no language signal land in the{' '}
                        <strong>Unknown</strong> bucket rather than being guessed.
                        First-party geo metadata replaces inference here when the
                        Phase 2 adapter for IP→geo ships.
                    </div>
                </>
            )}
        </>
    );
}
